/**
 * 演示站合成数据 — 批 6 包 G（PM 裁决：前端静态合成数据，不建演示租户、不发只读 JWT）。
 *
 * 全部数据来自概念稿 design/pages/demo.html：虚构品牌「星驰 X3 电动车」，
 * 竞品「风行者 S5」「蓝鲸 E7」。禁止出现任何真实品牌名。
 */
import type { AlertItem, LedgerSummary, MatrixCell } from "../../types";

/** 指标带 ×4（demo.html §指标带）。 */
export const DEMO_STATS = [
  {
    title: "AI 可见性（5 平台均值）",
    value: 71.8,
    trend: 6.3,
    subtitle: "vs 上周",
  },
  {
    title: "本周预警",
    value: 9,
    subtitle: "P0 ×3 · P1 ×6 · 近 24h 新增 4 条",
  },
  {
    title: "投放内容收录率",
    value: 72.9,
    suffix: "%",
    subtitle: "35/48 篇 · 高于基线 70%",
  },
  {
    title: "事实包",
    value: "v5",
    subtitle: "38 条事实条目 · A 级 26 · B 级 12",
  },
] as const;

/** 全景矩阵行列（demo.html §全景矩阵：6 话题 × 5 平台）。 */
export const DEMO_TOPICS = [
  "星驰 X3 冬季续航",
  "20 万级电动车推荐",
  "星驰 X3 智能驾驶",
  "家庭 SUV 电动车",
  "星驰 X3 充电速度",
  "星驰 vs 风行者",
];

export const DEMO_PLATFORMS = ["豆包", "DeepSeek", "通义千问", "元宝", "文心一言"];

/**
 * 矩阵单元格（分值照 demo.html 逐格转录）。
 * 元宝「星驰 X3 冬季续航」= 5 红格（l1），上期 68，环比 -63，P0 失联叙事；
 * 用 normal+低分呈现（概念稿单元格直接显示数值 5），失联徽标在卡头静态标注。
 */
const MATRIX_ROWS: number[][] = [
  [80, 82, 74, 5, 78],
  [85, 79, 72, 58, 80],
  [92, 88, 84, 71, 86],
  [74, 68, 63, 56, 70],
  [81, 84, 76, 64, 72],
  [68, 55, 64, 47, 66],
];

export const DEMO_MATRIX_CELLS: MatrixCell[] = DEMO_TOPICS.flatMap(
  (topic, r) =>
    MATRIX_ROWS[r].map((score, c) => ({
      topic,
      platform: DEMO_PLATFORMS[c],
      score,
      // 仅「元宝·冬季续航」概念稿给出上期值（68），其余未知置 null
      prev_score:
        topic === "星驰 X3 冬季续航" && DEMO_PLATFORMS[c] === "元宝" ? 68 : null,
      sample_n: 12,
      status: "normal" as const,
    })),
);

/** 预警流 4 条（demo.html §预警流：P0 ×2 + P1 ×2）。 */
export const DEMO_ALERTS: AlertItem[] = [
  {
    id: "demo-alert-1",
    rule_code: "AR1",
    level: "P0",
    title: "「星驰 X3 冬季续航」元宝平台新增失联",
    summary: "上期提及率 68%，本期降至 5，连续 2 个采样批次未检出品牌",
    status: "new",
    count: 1,
    last_triggered_at: "2026-07-18T06:00:00",
  },
  {
    id: "demo-alert-2",
    rule_code: "AR3",
    level: "P0",
    title: "竞品「风行者 S5」在 DeepSeek 排名跃升",
    summary: "「20 万级电动车推荐」话题排名 #9 → #3，挤压我方露出位",
    status: "new",
    count: 1,
    last_triggered_at: "2026-07-17T18:00:00",
  },
  {
    id: "demo-alert-3",
    rule_code: "AR2",
    level: "P1",
    title: "「星驰 vs 风行者」元宝提及率持续低位",
    summary: "47%，连续两期低于 50，竞品蓝鲸 E7 提及走高",
    status: "new",
    count: 1,
    last_triggered_at: "2026-07-17T06:00:00",
  },
  {
    id: "demo-alert-4",
    rule_code: "AR4",
    level: "P1",
    title: "高权重引用源「车友之家」引用量下降",
    summary: "周环比 -28%，影响信源引用率维度得分",
    status: "new",
    count: 1,
    last_triggered_at: "2026-07-16T14:00:00",
  },
];

/** 三层漏斗（demo.html §漏斗：48 → 35 → 31，收录层高于 70% 基线）。 */
export const DEMO_FUNNEL: LedgerSummary = {
  produced: 48,
  indexed: 35,
  ai_retrievable: 31,
  index_rate: 0.729,
  retrieval_rate: 0.886,
  baseline_index_rate: 0.7,
};

export const DEMO_FUNNEL_NOTE =
  "收录层高于 70% 基线；AI 可检索层经三级话术管道验证（完整标题 → 独家事实点 → 自然提问）。";

/** 竞品对比条（demo.html §竞品对比：宽度为相对我方分值占比）。 */
export const DEMO_COMPETITORS = [
  { name: "星驰 X3", score: 71.8, widthPct: 100, self: true },
  { name: "风行者 S5", score: 64.2, widthPct: 89, self: false },
  { name: "蓝鲸 E7", score: 58.6, widthPct: 82, self: false },
] as const;

export const DEMO_COMPETITOR_NOTE =
  "风行者 S5 周环比 ▲5.1，差距收窄至 7.6 分；其 DeepSeek 平台排名跃升已触发 P0 预警，建议本周补充「20 万级电动车推荐」话题投放。";
